import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  interveneFilterAtom,
  interveneUserDetailAtom,
  interveneUserJourneyAtom,
  interveneProfileLoadingAtom,
} from '../state/interveneAtoms';
import { interveneService } from '../services/interveneService';
import { chatDebug } from '../utils/chatDebug';

function buildProfileLoadKey(phone, botNumber) {
  return phone ? `${phone}:${botNumber || ''}` : '';
}

/**
 * Loads profile drawer data lazily — only when the panel is open.
 * List/chat selection does not trigger get-user-details or user-journey.
 */
export default function useUserProfile({ isProfileOpen = false } = {}) {
  const filters = useRecoilValue(interveneFilterAtom);
  const [activeUser, setActiveUser] = useRecoilState(interveneUserDetailAtom);
  const [journey, setJourney] = useRecoilState(interveneUserJourneyAtom);
  const [isLoadingProfile, setIsLoadingProfile] = useRecoilState(interveneProfileLoadingAtom);
  const lastLoadedRef = useRef('');
  const latestProfileRequestRef = useRef(0);

  useEffect(() => {
    const phone = activeUser?.phone_number;
    const botNumber = filters.botNumber;
    const loadKey = buildProfileLoadKey(phone, botNumber);

    if (!phone) {
      setJourney([]);
      setIsLoadingProfile(false);
      lastLoadedRef.current = '';
      return undefined;
    }

    if (!isProfileOpen) {
      setIsLoadingProfile(false);
      return undefined;
    }

    if (loadKey === lastLoadedRef.current) {
      return undefined;
    }

    setJourney([]);

    let cancelled = false;
    const requestId = ++latestProfileRequestRef.current;

    const loadProfile = async () => {
      setIsLoadingProfile(true);
      try {
        chatDebug('profile-request-start', {
          requestId,
          loadKey,
          phone,
          botNumber,
          isProfileOpen,
        });

        const [detailRes, journeyRes] = await Promise.all([
          interveneService.getUserDetail(phone),
          interveneService.getUserJourney(phone, botNumber),
        ]);

        const isStale = cancelled || requestId !== latestProfileRequestRef.current;
        if (isStale) {
          chatDebug(
            'profile-response-ignored',
            {
              reason: cancelled ? 'effect-cancelled' : 'stale-request',
              requestId,
              currentRequestId: latestProfileRequestRef.current,
              loadKey,
            },
            'warn'
          );
          return;
        }

        if (detailRes?.status === 200 && detailRes?.data && Object.keys(detailRes.data).length) {
          setActiveUser((prev) => {
            if (`${prev?.phone_number || ''}` !== `${phone}`) {
              chatDebug(
                'profile-detail-apply-rejected',
                {
                  reason: 'active-user-changed',
                  requestPhone: phone,
                  prevPhone: prev?.phone_number,
                  detailPhone: detailRes.data?.phone_number,
                  detailName: detailRes.data?.whatsapp_name,
                },
                'warn'
              );
              return prev;
            }

            chatDebug('profile-detail-apply', {
              requestPhone: phone,
              prevPhone: prev?.phone_number,
              detailPhone: detailRes.data?.phone_number,
              prevName: prev?.whatsapp_name,
              detailName: detailRes.data?.whatsapp_name,
            });

            return {
              ...prev,
              ...detailRes.data,
              phone_number: prev.phone_number,
              bot_number: botNumber || prev?.bot_number,
            };
          });
        }

        if (journeyRes?.status === 200) {
          setJourney(Array.isArray(journeyRes.data) ? journeyRes.data : []);
        } else {
          setJourney([]);
        }

        lastLoadedRef.current = loadKey;
      } catch (err) {
        console.error('Failed to load user profile', err);
        if (!cancelled) setJourney([]);
      } finally {
        if (!cancelled) setIsLoadingProfile(false);
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [
    activeUser?.phone_number,
    filters.botNumber,
    isProfileOpen,
    setActiveUser,
    setJourney,
    setIsLoadingProfile,
  ]);

  return {
    activeUser,
    journey,
    isLoadingProfile,
  };
}

export { buildProfileLoadKey };
